import { createContext, ReactNode, useContext, useMemo } from 'react';
import { ChartConfig, ChartId, DimensionId } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useChartData } from '../Charts/hooks/useChartData';
import { useDashboardView } from './DashboardViewContext';

// The chart one dashboard card draws, as every part of the card must request it. Held once here
// so no part can build a different render query (a duplicate request) by passing other values.
interface CardChart {
  chartId: ChartId;
  previewConfig?: ChartConfig;
  ignoreDashboardFilters?: DimensionId[];
}

const CardChartContext = createContext<CardChart | undefined>(undefined);

interface CardChartProviderProps extends CardChart {
  children: ReactNode;
}

export const CardChartProvider = ({
  chartId,
  previewConfig,
  ignoreDashboardFilters,
  children
}: CardChartProviderProps) => {
  const value = useMemo(
    () => ({ chartId, previewConfig, ignoreDashboardFilters }),
    [chartId, previewConfig, ignoreDashboardFilters]
  );
  return <CardChartContext.Provider value={value}>{children}</CardChartContext.Provider>;
};

export const useThisCard = (): CardChart => {
  const card = useContext(CardChartContext);
  if (!card) {
    throw new Error('useThisCard must be used within a CardChartProvider');
  }
  return card;
};

// A card's render query: its chart through the dashboard's current view and any working copy. Every
// part of the card reads it through useThisCardChartData, so they share one request.
const useCardChartData = (
  chartId: ChartId,
  previewConfig?: ChartConfig,
  // Per card, not per chart: the same chart can be placed twice with different opt-outs.
  ignoreDashboardFilters?: DimensionId[]
) => {
  const { cid: company } = useCompanyInfo();
  const {
    resolveRange,
    resolveComparisonRange,
    perChartRange,
    resolveKpi,
    resolveBreakdown,
    dashboardFilters
  } = useDashboardView();
  return useChartData(
    company,
    chartId,
    {
      dateRange: resolveRange(chartId),
      comparisonRange: resolveComparisonRange(chartId),
      // A range this card set for itself outranks the chart's own pinned window; the dashboard's
      // global range does not. `resolveRange` collapses the two, so say which one this is. Not part
      // of the cache key, which is exactly why every caller has to pass it the same way.
      dateRangeIsExplicit: perChartRange[chartId] !== undefined,
      kpiId: resolveKpi(chartId),
      breakdown: resolveBreakdown(chartId),
      dashboardFilters,
      ignoreDashboardFilters
    },
    previewConfig
  );
};

// The card's shared render query.
export const useThisCardChartData = () => {
  const { chartId, previewConfig, ignoreDashboardFilters } = useThisCard();
  return useCardChartData(chartId, previewConfig, ignoreDashboardFilters);
};
